import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Users, BookOpen, User, Search } from "lucide-react";
import { getApiUrl } from "../../config/api";
import { useAutoDismiss } from "../../hooks/useAutoDismiss";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";

interface Teacher {
  id: number;
  name: string;
  email: string;
  createdAt: string;
  classesCount: number;
  subjectsCount: number;
}

interface ClassAssignment {
  classId: number;
  className: string;
  classTeacherId: number | null;
  teacherName: string | null;
}

interface SubjectAssignment {
  subjectId: number;
  subjectName: string;
  teacherId: number | null;
  teacherName: string | null;
}

const HeadteacherTeachers: React.FC = () => {
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [classAssignments, setClassAssignments] = useState<ClassAssignment[]>([]);
  const [subjectAssignments, setSubjectAssignments] = useState<SubjectAssignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  useAutoDismiss(message, setMessage, null, 5000);

  const token = localStorage.getItem("token");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(getApiUrl("headteacher/teachers"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeachers(res.data?.teachers || []);
      setClassAssignments(res.data?.classAssignments || []);
      setSubjectAssignments(res.data?.subjectAssignments || []);
    } catch (error: any) {
      setMessage(error.response?.data?.message || "Error fetching teachers data");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredTeachers = teachers.filter(teacher =>
    teacher.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    teacher.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getTeacherClasses = (teacherId: number) => {
    return classAssignments.filter(c => c.classTeacherId === teacherId);
  };

  const getTeacherSubjects = (teacherId: number) => {
    return subjectAssignments.filter(s => s.teacherId === teacherId);
  };

  const totalTeachers = teachers.length;
  const totalAssignments = classAssignments.length + subjectAssignments.length;

  return (
    <div className="dashboard-container">
      <main className="dashboard-main">
        <div className="utopia-page-container">
          <div className="utopia-table-header">
            <div>
              <h2>Teachers Overview</h2>
              <p className="page-subtitle">View and manage teaching staff and their assignments.</p>
            </div>
          </div>

          {message && (
            <div className="p-4 mb-4 rounded-xl bg-red-50 text-red-700 text-sm">
              {message}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
              <p className="text-sm text-slate-500">Total Teachers</p>
              <p className="text-2xl font-bold text-slate-900">{totalTeachers}</p>
            </div>
            <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
              <p className="text-sm text-slate-500">Class Assignments</p>
              <p className="text-2xl font-bold text-teal-600">{classAssignments.length}</p>
            </div>
            <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
              <p className="text-sm text-slate-500">Subject Assignments</p>
              <p className="text-2xl font-bold text-blue-600">{subjectAssignments.length}</p>
            </div>
          </div>

          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                placeholder="Search teachers..."
                className="utopia-input pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center text-slate-500">Loading teachers data...</div>
          ) : (
            <div className="space-y-6">
              {filteredTeachers.map((teacher) => {
                const teacherClasses = getTeacherClasses(teacher.id);
                const teacherSubjects = getTeacherSubjects(teacher.id);
                return (
                  <div key={teacher.id} className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                          <User className="text-teal-600" size={24} />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-slate-900">{teacher.name}</h3>
                          <p className="text-sm text-slate-500">{teacher.email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-500">Classes: {teacherClasses.length}</p>
                        <p className="text-sm text-slate-500">Subjects: {teacherSubjects.length}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      {teacherClasses.length > 0 && (
                        <div className="bg-slate-50 p-4 rounded-xl">
                          <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                            <Users size={16} /> Class Teacher Assignments
                          </h4>
                          <div className="space-y-1">
                            {teacherClasses.map((cls) => (
                              <div key={cls.classId} className="flex items-center justify-between text-sm">
                                <span className="text-slate-600">{cls.className}</span>
                                <span className="text-xs px-2 py-0.5 rounded bg-teal-100 text-teal-700">
                                  Class Teacher
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {teacherSubjects.length > 0 && (
                        <div className="bg-slate-50 p-4 rounded-xl">
                          <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                            <BookOpen size={16} /> Subject Teaching
                          </h4>
                          <div className="space-y-1">
                            {teacherSubjects.map((subj) => (
                              <div key={subj.subjectId} className="flex items-center justify-between text-sm">
                                <span className="text-slate-600">{subj.subjectName}</span>
                                <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                                  Teacher
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {teacherClasses.length === 0 && teacherSubjects.length === 0 && (
                        <div className="col-span-2 text-center text-sm text-slate-500 py-4">
                          No assignments found for this teacher.
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {filteredTeachers.length === 0 && (
                <div className="text-center text-slate-500 py-12">
                  No teachers found matching your search.
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default HeadteacherTeachers;











